import { useCallback, useState } from 'react';
import { Crown, Info, Loader2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@scio3/ui';
import { APP_TYPES } from '@scio3/shared-types';
import type { AppMeta } from '@scio3/shared-types';
import { SafeHtml } from '@/components/shared/SafeHtml';
import { usePromotionalBanner } from '@/hooks/usePromotionalBanner';
import { useSubscriptionModal } from '@/hooks/useSubscriptionModal';
import { analyticsUpgradeCta } from '@/lib/analytics-service';
import { useAuthStore } from '@/stores/auth-store';
import { useSmallAppEditorStore } from '@/stores/smallapp-editor-store';

/**
 * If the promo HTML contains a single <a> wrapping all text
 * (e.g. "<a href='...'>Plain text. CTA!</a>"), pull the plain text outside the
 * link so only the last sentence (the CTA like "Explore Now!") is underlined.
 */
export function splitPromoLink(html: string): string {
  // Match an <a ...>...</a> anywhere in the string, capturing the opening tag and inner text
  const match = html.match(/(<a\s[^]*?>)([^]*?)<\/a>/i);
  if (!match) return html;

  const fullMatch = match[0]; // full <a ...>...</a>
  const aOpen = match[1]; // <a href="...">
  const inner = match[2]; // inner text content
  const lastDotSpace = inner.lastIndexOf('. ');

  if (lastDotSpace === -1) return html;

  const plainPart = inner.slice(0, lastDotSpace + 2);
  const ctaPart = inner.slice(lastDotSpace + 2);

  return html.replace(fullMatch, `${plainPart}${aOpen}${ctaPart}</a>`);
}

/**
 * Wrap the first sentence of a promo string in <strong> so it stands out while the
 * rest stays normal weight (SafeHtml allows <strong>). Splits on the first ". ".
 */
export function boldFirstSentence(html: string): string {
  const match = html.match(/[.!?]\s/);
  if (!match || match.index === undefined) return html;
  const end = match.index + 1; // include the sentence-ending punctuation
  return `<strong>${html.slice(0, end)}</strong>${html.slice(end)}`;
}

/**
 * Trial countdown with just the day count bolded inline (no badge), no trailing period.
 * Splits the fully-translated `waitingForTrial` string around the numeric day count
 * (locale-safe — the number always renders as digits) rather than introducing a new
 * i18n key, because prod loads i18n from the CDN where a new key/markup wouldn't
 * resolve without a Lokalise sync. Natural spacing is preserved by not trimming the
 * text around the number.
 */
function TrialCountdown({ daysLeft }: { daysLeft: number }) {
  const { t } = useTranslation();
  const text = t('waitingForTrial', { getDayTrial: daysLeft });
  const numStr = String(daysLeft);
  const idx = text.indexOf(numStr);
  const stripTrailingDot = (s: string) => s.replace(/[.。]\s*$/, '');

  if (idx === -1) {
    return <span>{stripTrailingDot(text).trim()}</span>;
  }

  const before = text.slice(0, idx);
  const after = stripTrailingDot(text.slice(idx + numStr.length));

  return (
    <span>
      {before}
      <span className="font-bold">{numStr}</span>
      {after}
    </span>
  );
}

export interface PromotionalBannerViewProps {
  bannerType: 'trial' | 'free' | 'paid';
  bannerHtml: string | null;
  daysLeft: number;
  showUpgrade: boolean;
  upgrading?: boolean;
  onUpgrade: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onDismiss?: () => void;
  /** Optional id-delegation click handler on the banner body (prod wires this). */
  onBannerClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

/**
 * Pure presentational promotional banner. Production wiring lives in
 * `PromotionalBanner` (fed from `usePromotionalBanner`); the dev Banner Catalog
 * renders this directly with forced props so previews stay byte-identical to prod.
 */
export function PromotionalBannerView({
  bannerType,
  bannerHtml,
  daysLeft,
  showUpgrade,
  upgrading = false,
  onUpgrade,
  onDismiss,
  onBannerClick,
}: PromotionalBannerViewProps) {
  const { t } = useTranslation();

  if (bannerType === 'trial') {
    return (
      <div className="hidden sm:block">
        {/* Trial banner is countdown-focused: no center promo, so users focus on the
            trial status + upgrade. Light mode: mint→primary tint gradient (#E8F7F4 →
            #98DDCF) with dark text. Dark mode: deep blue-leaning teal gradient
            (#0B302C → #1B6F64) with light text — a balanced teal with a subtle blue
            undertone; the wider light/dark range keeps the gradient visible without
            being too bright. */}
        <div
          className="flex h-12 w-full items-center justify-center gap-4 bg-gradient-to-l from-[#DFF4EF] to-[#98DDCF] px-6 text-sm text-[#313a46] dark:from-[#0B302C] dark:to-[#1B6F64] dark:text-white"
          role="alert"
        >
          <span className="flex items-center gap-2">
            <Info className="h-5 w-5 shrink-0" aria-hidden="true" />
            <TrialCountdown daysLeft={daysLeft} />
          </span>
          {showUpgrade && (
            <Button
              size="sm"
              onClick={onUpgrade}
              disabled={upgrading}
              data-testid="trial-banner-cta-btn"
              className="h-8 shrink-0 cursor-pointer rounded-md bg-[#313a46] px-5 text-xs font-semibold text-white transition-colors hover:bg-[#3d4856] dark:bg-slate-200 dark:text-[#313a46] dark:hover:bg-slate-300"
            >
              {upgrading && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
              {/* Neutral, structured key (groups nicely in Lokalise) instead of an
                  English-string key, so the copy can change without renaming the key. */}
              {t('trial.cta.button')}
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (bannerType === 'free') {
    return (
      <div className="hidden sm:block">
        {/* Free plan: mint→primary gradient (deep teal in dark mode), centered message
            + divider + charcoal Upgrade Now (admins). Matches the trial banner. */}
        <div
          className="flex min-h-12 w-full items-center justify-center gap-4 bg-gradient-to-l from-[#DFF4EF] to-[#98DDCF] px-6 py-2 text-sm font-medium text-[#313a46] [&_a]:text-[#313a46] [&_a]:underline [&_p]:mb-0 [&_p]:text-sm dark:from-[#0B302C] dark:to-[#1B6F64] dark:text-white dark:[&_a]:text-white"
          role="alert"
          onClick={onBannerClick}
        >
          {bannerHtml && (
            <SafeHtml
              html={boldFirstSentence(t(bannerHtml))}
              as="span"
              className="truncate text-center"
            />
          )}
          {showUpgrade && (
            <Button
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onUpgrade(e);
              }}
              disabled={upgrading}
              className="hidden md:inline-flex h-8 shrink-0 cursor-pointer rounded-md bg-[#313a46] px-5 text-xs font-semibold text-white transition-colors hover:bg-[#3d4856] dark:bg-slate-200 dark:text-[#313a46] dark:hover:bg-slate-300"
            >
              {upgrading && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
              {t('Upgrade Now')}
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Paid plan — feature-release announcement: mint→primary gradient (deep teal in dark
  // mode) + crown, matching the trial/free banners. The promo (including its CTA link)
  // renders inline and underlined — no extracted button, since the backend wraps the
  // CTA in the promo HTML itself.
  return (
    <div className="hidden sm:block">
      <div
        className="relative flex h-12 w-full items-center justify-center gap-3 bg-gradient-to-l from-[#DFF4EF] to-[#98DDCF] px-6 text-sm font-medium text-[#313a46] [&_a]:cursor-pointer [&_a]:text-[#313a46] [&_a]:underline [&_[id]]:cursor-pointer [&_p]:mb-0 [&_p]:text-sm dark:from-[#0B302C] dark:to-[#1B6F64] dark:text-white dark:[&_a]:text-white"
        role="alert"
        onClick={onBannerClick}
      >
        <Crown className="h-4 w-4 shrink-0 fill-current" aria-hidden="true" />
        {bannerHtml && <SafeHtml html={t(bannerHtml)} as="span" className="truncate text-center" />}

        {/* Close button */}
        <Button
          data-testid="dismiss-banner-btn"
          variant="ghost"
          size="icon-sm"
          className="absolute right-3 shrink-0 rounded-full p-1 text-[#313a46] transition-colors hover:bg-black/10 dark:text-white dark:hover:bg-white/10"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss?.();
          }}
          aria-label={t('Dismiss banner')}
        >
          <X className="h-4 w-4" strokeWidth={1.5} />
        </Button>
      </div>
    </div>
  );
}

export function PromotionalBanner() {
  const { t } = useTranslation();
  const { isVisible, bannerType, bannerHtml, daysLeft, showUpgrade, dismiss } =
    usePromotionalBanner();
  const { openSubscriptionPlan } = useSubscriptionModal();
  const company = useAuthStore((s) => s.company);
  const me = useAuthStore((s) => s.me);
  const [upgrading, setUpgrading] = useState(false);

  /** Delegate clicks on banner HTML elements by id (matches scio1 dashboard.js onBannerClick) */
  const handleBannerClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = (e.target as HTMLElement).closest('[id]');
      if (!target) return;

      if (target.id === 'openBackgroundMusic') {
        e.preventDefault();
        e.stopPropagation();
        const appMeta = me?.appMetas?.find(
          (m: { appType?: string }) => m.appType === APP_TYPES.BACKGROUND_MUSIC,
        );
        if (!appMeta) return;
        useSmallAppEditorStore.getState().openEditor({
          appMeta: appMeta as AppMeta,
          asset: { appType: APP_TYPES.BACKGROUND_MUSIC },
          options: { isCloseModalAfterSave: true },
        });
      }
    },
    [me],
  );

  if (!isVisible || !bannerType) return null;
  if (bannerType !== 'trial' && !bannerHtml) return null;

  const handleUpgrade = async () => {
    if (upgrading) return;
    setUpgrading(true);
    analyticsUpgradeCta({
      ctaLocation: `promotional_banner_${bannerType}`,
      currentPlan: company?.payment?.plan,
    });
    try {
      // No fixed delay needed — scio1 subscriptionPlanModal now subscribes to
      // 'myCompany' reactively + showStep() guards against null data (#718).
      await openSubscriptionPlan({
        // Proactive entry — user clicked Upgrade Now in the banner, not feature-gated.
        // Override the wizard's default "Upgrade Required" title with friendlier scio1 wording.
        // Use `wizardTitle` (NOT `title`) so feature-gate callers that still pass
        // `title: t('Change Plan')` for the legacy iframe path are unaffected.
        wizardTitle: t('Select Your Plan'),
        // Match scio1 dashboard.js openSubscriptionPlanModal: use changePlanTrialToPay for
        // both trial and free plan users from the banner. changePlanPay triggers reactive
        // billing email validation in subscriptionPlanModal.js for accounts without a card.
        action: bannerType === 'paid' ? 'changePlanPay' : 'changePlanTrialToPay',
        sizeModal: 'xl',
        params: {
          arrCssFile: true,
          // Filter plans to standard+ and pre-select proPlus (matches scio1 dashboard.js)
          planFeature: 'proPlus',
          minPlanRequired: 'standard',
          // Wizard-only signal: highlight Pro Plus on the plan-comparison step.
          // Distinct from `planFeature` (which is overloaded by feature-gate callers
          // to echo the user's current plan) — opt-in only.
          recommendedPlan: 'proPlus',
        },
      });
    } finally {
      setUpgrading(false);
    }
  };

  return (
    <PromotionalBannerView
      bannerType={bannerType}
      bannerHtml={bannerHtml}
      daysLeft={daysLeft}
      showUpgrade={showUpgrade}
      upgrading={upgrading}
      onUpgrade={handleUpgrade}
      onDismiss={dismiss}
      onBannerClick={handleBannerClick}
    />
  );
}
